/* WebComponentAccordionGallerySlide component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Method, Prop, Watch } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import AccordionGallerySlideTemplate from './templates/cwc-accordion-gallery-slide.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { InfoBoxChangeDetail, SlideState, StateChangeDetail } from './states';

/**
 * @slot horizontal-accordion-close-icon - HTML element used as caption for the button to close a gallery slide in horizontal mode e.g. an img or svg
 * @slot vertical-accordion-close-icon - HTML element used as caption for the button to close a gallery slide in vertical mode e.g. an img or svg
 * @slot vertical-accordion-info-icon - HTML element used as caption for the button to open a gallery slide in vertical mode e.g. an img or svg
 */
@Component({
  tag: 'cwc-accordion-gallery-slide',
  styleUrl: './styles/cwc-accordion-gallery-slide.scss',
  shadow: true,
})
export class AccordionGallerySlideComponent {
  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * indicates if slide is open
   */
  @Prop({
    reflect: true,
    mutable: true,
  })
  state: string = SlideState.Closed;

  @Prop()
  tabBackground: string;

  /**
   * indicates if infobox is open
   */
  @Prop({
    reflect: true,
    mutable: true,
  })
  infoBoxOpened: boolean = false;

  @Prop()
  tabTitle: string;

  /**
   * Event thrown if the slide state changed
   *
   * @docs
   * @event on:accordion-gallery-slide:statechange
   */
  @Event({
    eventName: 'on:accordion-gallery-slide:statechange',
  })
  onAccordionGallerySlideStateChange: EventEmitter<StateChangeDetail>;

  /**
   * Event thrown if the state of the infobox changed
   *
   * @docs
   * @event on:accordion-gallery-slide:infoboxchange
   */
  @Event({
    eventName: 'on:accordion-gallery-slide:infoboxchange',
  })
  onAccordionGallerySlideInfoBoxChange: EventEmitter<InfoBoxChangeDetail>;

  /**
   * @constructor
   */
  constructor() {}

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return (
      <AccordionGallerySlideTemplate
        controller={this}
        alwaysOpen={this.element.parentElement.hasAttribute('always-open')}
      />
    );
  }

  toggleInfoBox() {
    const oldState = this.infoBoxOpened,
      newState = !this.infoBoxOpened;
    this.infoBoxOpened = newState;

    this.onAccordionGallerySlideInfoBoxChange.emit({
      element: this.element,
      oldState: oldState,
      newState: newState,
    });
    // console.log('infobox changed to opened', this.infoBoxOpened);
  }

  toggleState() {
    if (this.state == SlideState.Opened) {
      this.close();
    } else {
      this.open();
    }
  }

  @Watch('state')
  observeState(newState: string, oldState: string) {
    this.onAccordionGallerySlideStateChange.emit({
      element: this.element,
      backgroundSelector: this.tabBackground,
      oldState: oldState,
      newState: newState,
    });
    // console.log('state changed from', oldState, 'to', newState);
  }

  @Method()
  async open() {
    this.state = SlideState.Opened;
  }

  @Method()
  async close() {
    this.state = SlideState.Closed;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(AccordionGallerySlideComponent.prototype, AbstractComponent.prototype);

export interface AccordionGallerySlideComponent extends AbstractComponent {}
